import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { QuestionMarkCircleIcon, ArrowRightIcon, ArrowDownTrayIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import PersonalInfoSection from './settings/PersonalInfoSection';
import PasswordSection from './settings/PasswordSection';
import ConfigurationSection from './settings/ConfigurationSection';
import { useDriver } from '../../../contexts/DriverContext';
import { usePWAInstall } from '../../../hooks/usePWAInstall';
import { useState } from 'react';

const DriverSettings = ({ profileImage, handleLogout }) => {
  const { t } = useTranslation();
  const { driverAccount } = useDriver();
  const { canInstall, isInstalled, isIOS, promptInstall } = usePWAInstall();
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  const userData = {
    name: driverAccount?.name || "",
    email: driverAccount?.email || "",
    phone: driverAccount?.phone || "",
    information_sharing: driverAccount?.information_sharing || false,
    legal_status: driverAccount?.customer_subtype || "UNDEFINED"
  };
  console.log('DriverSettings userData:', userData);
  const handleInstallClick = async () => {
    const result = await promptInstall();

    if (result.outcome === 'ios-instructions-needed') {
      setShowIOSInstructions(true);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left Column */}
      <div className="space-y-6">
        {/* Personal Info Section */}
        <PersonalInfoSection userData={userData} />

        {/* Password Section */}
        <PasswordSection />

        {/* Support Section */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
          {/* Header */}
          <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <QuestionMarkCircleIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {t('sidebar.support')}
                </h2>
              </div>
            </div>
          </div>

          <div className="p-4">
            {/* <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
              {t('settings.supportDescription', 'Need help? Visit our support page for assistance, FAQs, and contact information.')}
            </p> */}
            <Link
              to="/driver/support"
              className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm shadow-sm hover:shadow-md group"
            >
              <span>{t('settings.goToSupport')}</span>
              <ArrowRightIcon className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
            </Link>
          </div>
        </div>

        {/* PWA Install Section */}
        {canInstall && !isInstalled && (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
            {/* Header */}
            <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-purple-50 dark:bg-purple-900/20 rounded-t-xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <ArrowDownTrayIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                    {t('settings.pwaInstall.title')}
                  </h2>
                </div>
              </div>
            </div>

            <div className="p-6">
              <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
                {t('settings.pwaInstall.description')}
              </p>

              {showIOSInstructions && isIOS ? (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 mb-4">
                  <p className="text-sm text-blue-900 dark:text-blue-200 font-medium mb-2">
                    {t('settings.pwaInstall.iosInstructions.title')}
                  </p>
                  <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800 dark:text-blue-300">
                    <li>{t('settings.pwaInstall.iosInstructions.step1')}</li>
                    <li>{t('settings.pwaInstall.iosInstructions.step2')}</li>
                    <li>{t('settings.pwaInstall.iosInstructions.step3')}</li>
                  </ol>
                </div>
              ) : (
                <button
                  onClick={handleInstallClick}
                  className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm shadow-sm hover:shadow-md group"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  <span>{t('settings.pwaInstall.installButton')}</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Logout Section */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
          {/* Header */}
          <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 bg-rose-50 dark:bg-rose-900/20 rounded-t-xl">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
                <ArrowRightOnRectangleIcon className="w-5 h-5 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {t('sidebar.logout')}
                </h2>
              </div>
            </div>
          </div>

          <div className="p-6">
            <p className="text-slate-600 dark:text-slate-400 mb-4 text-sm">
              {t('settings.logoutDescription', 'Sign out of your account and return to the login screen.')}
            </p>
            <button
              onClick={handleLogout}
              className="inline-flex items-center px-4 py-2 bg-rose-600 hover:bg-rose-700 dark:bg-rose-600 dark:hover:bg-rose-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm shadow-sm hover:shadow-md group"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
              <span>{t('sidebar.logout')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className="space-y-6">
        {/* Configuration Section */}
        <ConfigurationSection userData={userData} />
      </div>
    </div>
  );
};

export default DriverSettings;